import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bug,
  CircleHelp,
  Lightbulb,
  MessageSquare,
  MessagesSquare,
  Send,
} from "lucide-react";
import { PageHeader } from "@/components/wingo/ui/page-header";
import { Card } from "@/components/wingo/ui/card";
import { Button } from "@/components/wingo/ui/button";
import { EmptyState } from "@/components/wingo/ui/empty-state";
import { SegmentedControl } from "@/components/wingo/ui/segmented-control";
import { Textarea } from "@/components/wingo/ui/textarea";
import { Skeleton } from "@/components/wingo/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCompany } from "@/contexts/CompanyContext";
import { useDirection } from "@/contexts/DirectionContext";
import {
  contactApi,
  CONTACT_MESSAGE_MAX,
  CONTACT_MESSAGE_MIN,
  type ContactMessageType,
} from "../api/contact.api";

type Localized = { ro: string; en: string };

const COPY = {
  title: { ro: "Contact", en: "Contact" },
  description: {
    ro: "Ai găsit o eroare, ai o sugestie sau o întrebare? Scrie-ne și mesajul ajunge direct la echipa TLS.",
    en: "Found an error, have a suggestion or a question? Write to us and your message goes straight to the TLS team.",
  },
  typeLabel: { ro: "Tipul mesajului", en: "Message type" },
  messageLabel: { ro: "Mesaj", en: "Message" },
  messagePlaceholder: {
    ro: "Descrie pe scurt ce ai observat sau ce ți-ar fi util. Dacă e vorba de un container sau de un B/L, scrie și numărul lui.",
    en: "Briefly describe what you noticed or what would help you. If it is about a container or a B/L, include its number.",
  },
  tooShort: {
    ro: `Scrie cel puțin ${CONTACT_MESSAGE_MIN} caractere.`,
    en: `Write at least ${CONTACT_MESSAGE_MIN} characters.`,
  },
  privacy: {
    ro: "Împreună cu mesajul trimitem numele, adresa de email și firmele contului tău, ca să-ți putem răspunde.",
    en: "Along with the message we send your account name, email address and companies, so we can reply.",
  },
  send: { ro: "Trimite mesajul", en: "Send message" },
  sending: { ro: "Se trimite", en: "Sending" },
  sentTitle: { ro: "Mesaj trimis", en: "Message sent" },
  sentDescription: {
    ro: "Mulțumim! Echipa TLS a primit mesajul și îți răspunde pe email, dacă e nevoie.",
    en: "Thank you! The TLS team has received your message and will reply by email if needed.",
  },
  another: { ro: "Trimite alt mesaj", en: "Send another message" },
  offTitle: {
    ro: "Formularul nu este disponibil momentan",
    en: "The form is not available right now",
  },
  offDescription: {
    ro: "Ne poți scrie oricând la sales@tls-romania.com sau direct agentului tău de vânzări TLS.",
    en: "You can always write to sales@tls-romania.com or directly to your TLS sales agent.",
  },
  genericError: {
    ro: "Mesajul nu a putut fi trimis. Încearcă din nou în câteva minute.",
    en: "The message could not be sent. Please try again in a few minutes.",
  },
} satisfies Record<string, Localized>;

const TYPE_LABELS: Record<ContactMessageType, Localized> = {
  error: { ro: "Eroare", en: "Error" },
  suggestion: { ro: "Sugestie", en: "Suggestion" },
  question: { ro: "Întrebare", en: "Question" },
  other: { ro: "Altceva", en: "Other" },
};

const TYPE_KEYS = Object.keys(TYPE_LABELS) as ContactMessageType[];

const TYPE_ICONS: Record<ContactMessageType, React.ReactNode> = {
  error: <Bug />,
  suggestion: <Lightbulb />,
  question: <CircleHelp />,
  other: <MessagesSquare />,
};

/** Pagina /app/contact: tip + mesaj. Emailul îl trimite aplicația principală (noreply → adresa din setări). */
const ContactPage: React.FC = () => {
  const { currentLanguage } = useLanguage();
  const lang: "ro" | "en" = currentLanguage === "en" ? "en" : "ro";
  const c = (key: keyof typeof COPY) => COPY[key][lang];
  const { selectedCompanyId } = useCompany();
  const { direction } = useDirection();

  const [type, setType] = useState<ContactMessageType>("suggestion");
  const [message, setMessage] = useState("");
  const [touched, setTouched] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ["contact", "status"],
    queryFn: contactApi.status,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const trimmed = message.trim();
  const tooShort = trimmed.length < CONTACT_MESSAGE_MIN;

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setTouched(true);
    if (tooShort || sending) return;
    setSending(true);
    setError(null);
    try {
      await contactApi.send({
        type,
        message: trimmed,
        companyId:
          typeof selectedCompanyId === "number" ? selectedCompanyId : undefined,
        direction,
      });
      setSent(true);
      setMessage("");
      setTouched(false);
    } catch (err) {
      const apiMessage = (
        err as { response?: { data?: { message?: unknown } } }
      )?.response?.data?.message;
      // Mesajele backendului sunt în română; pe engleză păstrăm textul generic.
      setError(
        lang === "ro" && typeof apiMessage === "string"
          ? apiMessage
          : c("genericError"),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 pb-10">
      <PageHeader
        icon={<MessageSquare />}
        tone="brand"
        title={c("title")}
        description={c("description")}
      />

      {status.isLoading ? (
        <Card variant="outline" className="space-y-4 p-5">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </Card>
      ) : !status.data?.enabled ? (
        <EmptyState
          preset="offline"
          variant="dashed"
          icon={<MessageSquare />}
          title={c("offTitle")}
          description={c("offDescription")}
        />
      ) : sent ? (
        <EmptyState
          variant="surface"
          tone="success"
          icon={<Send />}
          title={c("sentTitle")}
          description={c("sentDescription")}
          live
          animated
          actions={
            <Button variant="outline" onClick={() => setSent(false)}>
              {c("another")}
            </Button>
          }
        />
      ) : (
        <Card variant="outline" className="p-5">
          <form className="space-y-5" onSubmit={submit} noValidate>
            <div className="space-y-2">
              <div
                className="text-sm font-medium text-foreground"
                id="contact-type-label"
              >
                {c("typeLabel")}
              </div>
              {/* telefon: grilă 2×2 (patru opțiuni nu încap pe un rând la 360px); de la sm în sus, selector segmentat */}
              <div
                className="grid grid-cols-2 gap-2 sm:hidden"
                role="radiogroup"
                aria-labelledby="contact-type-label"
              >
                {TYPE_KEYS.map((key) => (
                  <Button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={type === key}
                    variant="soft"
                    tone={type === key ? "brand" : "neutral"}
                    leftIcon={TYPE_ICONS[key]}
                    onClick={() => setType(key)}
                  >
                    {TYPE_LABELS[key][lang]}
                  </Button>
                ))}
              </div>
              <SegmentedControl
                className="hidden sm:flex"
                aria-labelledby="contact-type-label"
                value={type}
                onValueChange={(v) => setType(v as ContactMessageType)}
                fullWidth
                options={TYPE_KEYS.map((key) => ({
                  value: key,
                  label: TYPE_LABELS[key][lang],
                  icon: TYPE_ICONS[key],
                }))}
              />
            </div>

            <Textarea
              label={c("messageLabel")}
              placeholder={c("messagePlaceholder")}
              value={message}
              onValueChange={setMessage}
              onBlur={() => setTouched(true)}
              minRows={6}
              maxRows={16}
              autosize
              showCount
              maxLength={CONTACT_MESSAGE_MAX}
              invalid={touched && tooShort}
              error={touched && tooShort ? c("tooShort") : undefined}
              submitShortcut="mod+enter"
              onSubmitShortcut={() => submit()}
            />

            <p className="text-xs text-muted-foreground">{c("privacy")}</p>

            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                tone="brand"
                leftIcon={<Send />}
                loading={sending}
                loadingText={c("sending")}
              >
                {c("send")}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};

export default ContactPage;
